import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Download, ShieldCheck, MousePointerClick } from "lucide-react";

export function SmartScreenMockup() {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="mx-auto w-full max-w-md select-none overflow-hidden rounded-lg bg-[#0078D4] text-white shadow-2xl"
      data-testid="smartscreen-mockup"
    >
      <div className="flex justify-end px-3 pt-2 text-sm leading-none text-white/80" aria-hidden="true">✕</div>
      <div className="px-6 pb-2">
        <p className="text-[22px] font-light leading-tight">Windows protected your PC</p>
        <p className="mt-3 text-[13px] leading-relaxed text-white/90">
          Publisher: <span className="font-semibold text-white">Unknown publisher</span>
        </p>
        {!expanded ? (
          <button
            type="button"
            data-testid="smartscreen-mockup-more-info"
            onClick={() => setExpanded(true)}
            className="mt-3 rounded px-1 text-[13px] font-semibold text-white underline underline-offset-2 ring-2 ring-[#00F0FF] ring-offset-2 ring-offset-[#0078D4] transition hover:text-white/90"
          >
            More info
          </button>
        ) : (
          <div className="mt-3 text-[13px] leading-relaxed text-white/90" data-testid="smartscreen-mockup-details">
            <p>
              App: <span className="text-white">PORTBRIDGE-Portal-Setup.exe</span>
            </p>
          </div>
        )}
      </div>
      <div className="mt-4 flex items-center justify-end gap-3 border-t border-white/15 bg-[#0067B8] px-6 py-3">
        {expanded && (
          <button
            type="button"
            className="border-2 border-white bg-transparent px-4 py-1.5 text-[13px] font-medium text-white ring-2 ring-[#00F0FF] ring-offset-2 ring-offset-[#0067B8]"
            data-testid="smartscreen-mockup-run-anyway"
            tabIndex={-1}
          >
            Run anyway
          </button>
        )}
        <button type="button" className="border-2 border-white/60 bg-transparent px-4 py-1.5 text-[13px] text-white" tabIndex={-1}>
          Don't run
        </button>
      </div>
    </div>
  );
}

export function SmartScreenContent() {
  return (
    <div className="space-y-6">
      <div className="flex gap-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0066FF]/15">
          <Download className="h-5 w-5 text-[#00F0FF]" />
        </span>
        <div>
          <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00F0FF]">Step 1</p>
          <h4 className="mt-1 font-display text-base font-bold text-white">Download and run the setup file</h4>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
            Save the file when your browser asks, then open it from your Downloads folder
            (e.g. <span className="font-mono text-slate-300">PORTBRIDGE-Portal-Setup.exe</span>) to start the installation.
          </p>
        </div>
      </div>

      <div className="flex gap-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#0066FF]/15">
          <ShieldCheck className="h-5 w-5 text-[#00F0FF]" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-[#00F0FF]">Step 2</p>
          <h4 className="mt-1 font-display text-base font-bold text-white">The Windows SmartScreen alert</h4>
          <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
            On first run, Windows may show a blue alert that says <span className="text-white">"Unknown publisher"</span>.
            This is standard for newly published software that has not yet built up a Microsoft reputation —
            it does <span className="text-white">not</span> mean the file is harmful.
          </p>
          <div className="mt-4">
            <SmartScreenMockup />
            <p className="mt-3 flex items-center justify-center gap-2 text-center text-xs text-slate-500">
              {<MousePointerClick className="h-3.5 w-3.5 text-[#00F0FF]" />}
              In the real alert: press <span className="font-semibold text-white">More info</span>, then press
              <span className="font-semibold text-white">Run anyway</span>
            </p>
          </div>
        </div>
      </div>

      <p className="border-t border-white/10 pt-4 text-center text-xs text-slate-500">
        Questions? Contact our operations desk — we'll walk you through it in minutes.
      </p>
    </div>
  );
}

export function SmartScreenDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] max-w-lg overflow-y-auto border-[#0066FF]/30 bg-[#0B172A] p-6 sm:p-8"
        data-testid="smartscreen-guide-modal"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold text-white">
            How to install the Carrier Portal
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-400">
            Two quick steps — takes under a minute.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <SmartScreenContent />
        </div>
      </DialogContent>
    </Dialog>
  );
}
